import { useEffect, useState, Fragment, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import { Empty, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { RightOutlined } from '@ant-design/icons';
import HistoryItem from './HistoryItem';
import CollectionList from './CollectionList/index';
import { getHistoryMeetingList } from '@/common/http/meet';
import { isSuccessCode } from '@/common/utils/response';
import { THistoryMeeting } from '@/common/basis/types/database/thistory-meeting';
import { TResponse } from '@/common/basis/types/tresponse';
import ModalErrorExit from '@/components/HOC/ModalErrorExit';
import MeetingDetail from '@/components/MeetingDetail';
import { TField } from '@/common/basis/types/tfield';

function App() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [historyList, setHistoryList] = useState<THistoryMeeting.Item[]>([]);
  const [isListEnd, setIsListEnd] = useState(false);
  const [isHistoryListLoading, setIsHistoryListLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollSwitchRef = useRef<HTMLDivElement>(null);
  const scrollTimerIdRef = useRef<NodeJS.Timeout | null>(null);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [isOpenHistoryDetail, setIsOpenHistroyDetail] = useState(false);
  const [isOpenCollectionList, setIsOpenCollectionList] = useState(false);
  const currentHistoryItem = useMemo(() => historyList[currentHistoryIndex], [currentHistoryIndex, historyList]);
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage({ top: 90 });
  const repeatType = useMemo(() => currentHistoryItem?.repeatType, [currentHistoryItem?.repeatType]);
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]); // 是否为周期会议
  const [isOpenErrorExit, setIsOpenErrorExit] = useState(false);
  const [exitErrorMsg, setExitErrorMsg] = useState('');

  // 加载列表
  const requestHistoryMeetingList = async () => {
    if (isHistoryListLoading || isListEnd) {
      return;
    }
    setIsHistoryListLoading(true);
    try {
      const resp = await getHistoryMeetingList({ page, pageSize });

      if (isSuccessCode(resp?.code)) {
        const list = resp.data.list;

        if (list.length >= pageSize) {
          setPage(page + 1);
        } else {
          setIsListEnd(true);
        }
        setHistoryList([...historyList, ...list]);
      } else if (resp?.code === TResponse.CODE_401) {
        setExitErrorMsg(resp?.msg);
      } else {
        messageApi.destroy();
        messageApi.error(resp?.msg);
      }
    } catch (error) {
      messageApi.destroy();
      messageApi.error(t('networkError'));
    }

    setIsHistoryListLoading(false);
  };

  // 按天去重
  const dayList = uniqBy(
    historyList.map((item) => {
      const startedTime = item?.startedTime;
      const date = dayjs(startedTime).format(t('MMDDFormat')).replace(/^0/, '');
      const day = dayjs(startedTime).format('ddd');

      return { date, day };
    }),
    'date',
  );

  useEffect(() => {
    const scorllFn = () => {
      const bottom = scrollSwitchRef.current?.getBoundingClientRect().bottom;

      if (scrollTimerIdRef.current) {
        clearTimeout(scrollTimerIdRef.current);
      }
      if (bottom && Math.floor(bottom) <= window.innerHeight + 30) {
        scrollTimerIdRef.current = setTimeout(() => requestHistoryMeetingList(), 500);
      }
    };

    contentRef.current?.addEventListener('scroll', scorllFn);

    return () => {
      contentRef.current?.removeEventListener('scroll', scorllFn);
    };
  }, [page, isHistoryListLoading]);

  useEffect(() => {
    if (exitErrorMsg) {
      setIsOpenErrorExit(true);
    }
  }, [exitErrorMsg]);

  useEffect(() => {
    if (!isOpenHistoryDetail) {
      setCurrentHistoryIndex(-1);
    }
  }, [isOpenHistoryDetail]);

  useEffect(() => {
    if (!isOpenErrorExit) {
      setExitErrorMsg('');
    }
  }, [isOpenErrorExit]);

  useEffect(() => {
    requestHistoryMeetingList();

    return () => {
      setHistoryList([]);
    };
  }, []);

  return (
    <>
      <div className='h-full overflow-y-auto' ref={contentRef}>
        <div className='p-3 pb-0'>
          {dayList.map((item) => {
            const currentHistoryList = historyList.filter(
              (value) => dayjs(value?.startedTime).format(t('MMDDFormat')).replace(/^0/, '') === item.date,
            );

            return (
              <Fragment key={item.date}>
                <div className='felx items-center justify-start pb-4 pt-1 text-[#131415]'>
                  <span className='mr-2 text-[16px] font-medium'>{item.date}</span>
                  <span className='text-[12px]'>{item.day}</span>
                </div>
                {currentHistoryList.map((value) => (
                  <HistoryItem
                    historyList={historyList}
                    index={historyList.findIndex((val) => val?.startedTime === value?.startedTime)}
                    key={value?.startedTime}
                    setCurrentHistoryIndex={setCurrentHistoryIndex}
                    setIsOpenHistroyDetail={setIsOpenHistroyDetail}
                  />
                ))}
              </Fragment>
            );
          })}
          {historyList.length ? (
            isListEnd ? (
              <div className='flex items-center justify-center py-3'>
                <span className='text-[#888]'>—— {t('beenLoaded')} ——</span>
              </div>
            ) : isHistoryListLoading ? (
              <div className='flex items-center justify-center py-5'>
                <Spin />
              </div>
            ) : (
              <></>
            )
          ) : isHistoryListLoading ? (
            <div className='flex items-center justify-center pt-10'>
              <Spin />
            </div>
          ) : (
            <Empty className='mt-20' description={t('noneMeeting')} />
          )}
          <div className='h-3' ref={scrollSwitchRef} />
        </div>
      </div>
      <MeetingDetail
        isOpen={isOpenHistoryDetail}
        roomId={currentHistoryItem?.roomId ?? ''}
        roomSid={currentHistoryItem?.roomSid ?? ''}
        setIsOpen={setIsOpenHistroyDetail}
      >
        {isRepeatMeeting ? (
          <div
            className='my-4 flex cursor-pointer items-center justify-between rounded-xl bg-[#fff] p-3 py-4'
            onClick={() => setIsOpenCollectionList(true)}
          >
            <div className='text-[#323233]'>
              <span>{t('historyMeetingCollection')}</span>
            </div>
            <div>
              <RightOutlined className='text-[rgba(10,10,23,.4)]' />
            </div>
          </div>
        ) : (
          <></>
        )}
      </MeetingDetail>
      <CollectionList
        currentHistoryIndex={currentHistoryIndex}
        historyList={historyList}
        isOpenCollectionList={isOpenCollectionList}
        setIsOpenCollectionList={setIsOpenCollectionList}
      />
      <ModalErrorExit isOpen={isOpenErrorExit} setIsOpen={setIsOpenErrorExit}>
        <div className='py-6 text-[14px] text-[#888]'>{exitErrorMsg}</div>
      </ModalErrorExit>
      {contextHolder}
    </>
  );
}

export default App;
